/**
 * Shared current-user context.
 * Centralizes user + tenant for the app. Leaf components should consume this
 * instead of calling User.me() or /api/user/me directly.
 *
 * - display_name, profile_photo_url: tenant-scoped (fallback: full_name, empty)
 * - id, email, full_name, tenant_id, role: global identity
 */

import React, { createContext, useContext, useMemo } from 'react';
import { getEffectiveTenant } from '@/lib/tenantUtils';
import { deriveCapabilities } from '@/lib/tenantCapabilities';

export const UserContext = createContext(null);
export const CurrentTenantContext = createContext(null);
/** Session tenant flags from GET /api/auth/session → tenant */
export const SessionTenantContext = createContext(null);

export function useUser() {
  return useContext(UserContext);
}

export function useCurrentTenant() {
  return useContext(CurrentTenantContext);
}

export function useSessionTenant() {
  return useContext(SessionTenantContext);
}

/** Product capabilities (showCore / showProcure / defaultRoute) from session tenant. */
export function useTenantCapabilities() {
  const sessionTenant = useSessionTenant();
  return useMemo(() => deriveCapabilities(sessionTenant), [sessionTenant]);
}

/**
 * Combined hook for user + tenant. Use this in leaf components instead of User.me().
 * Returns safe values: displayName falls back to full_name, profilePhotoUrl to empty.
 */
export function useUserContext() {
  const user = useUser();
  const currentTenant = useCurrentTenant();

  return useMemo(() => {
    const tenantId = currentTenant || getEffectiveTenant(user) || 'instructure';
    return {
      user,
      currentTenant: tenantId,
      id: user?.id,
      email: user?.email,
      full_name: user?.full_name,
      tenant_id: user?.tenant_id,
      display_name: user?.display_name || user?.full_name || '',
      profile_photo_url: user?.profile_photo_url || '',
      role: user?.role,
      isAuthenticated: !!user?.id,
      isLoading: false,
    };
  }, [user, currentTenant]);
}
